import { enabledSizes, sizeCmFromName, type PizzaConfig } from '@agos/shared'

/**
 * Merges the franchise's configured pizza sizes with the sizes a product has
 * actually been priced for.
 *
 * The three rules are ported exactly, and each one prevents a different kind of
 * silent damage:
 *
 *   1. **A stored size stays offered.** It is already on the menu and somebody
 *      priced it.
 *   2. **A configured size the product has never been priced for is offered
 *      only on a NEW product.** On an existing one it defaults to *not*
 *      offered — otherwise adding a size to the configuration would silently
 *      put every pizza on the menu at that size, at a blank price.
 *   3. **A stored size the configuration no longer enables is still shown.**
 *      It is dropping off the menu; hiding the row would mean nobody could see
 *      what they were losing.
 */

export interface PizzaSizeRow {
  label: string
  weightG: string
  price: string
  offered?: boolean
}

export const DEFAULT_PIZZA_SIZE_ROWS: PizzaSizeRow[] = [
  { label: '32cm', weightG: '', price: '', offered: true },
  { label: '50cm', weightG: '', price: '', offered: true },
]

export function pizzaSizeRows(
  config: PizzaConfig | null,
  storedSizesJson: string | null,
  isNewProduct: boolean,
): PizzaSizeRow[] {
  let stored: PizzaSizeRow[] = []
  if (storedSizesJson) {
    try {
      stored = (JSON.parse(storedSizesJson) as PizzaSizeRow[]).map((size) => ({
        ...size,
        offered: true,
      }))
    } catch {
      // A malformed blob is treated as no stored sizes rather than crashing the
      // product dialog.
    }
  }

  const configured = enabledSizes(config)
  if (configured.length === 0) {
    return stored.length > 0 ? stored : DEFAULT_PIZZA_SIZE_ROWS.map((size) => ({ ...size }))
  }

  const rows: PizzaSizeRow[] = configured.map((configuredSize) => {
    const match = stored.find((size) => sizeCmFromName(size.label) === configuredSize.cm)
    return (
      match ?? {
        label: configuredSize.label,
        weightG: '',
        price: '',
        offered: isNewProduct,
      }
    )
  })

  for (const size of stored) {
    const cm = sizeCmFromName(size.label)
    if (cm == null || !configured.some((configuredSize) => configuredSize.cm === cm)) {
      rows.push(size)
    }
  }

  return rows
}
